import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import SearchInput from '../components/common/SearchInput';
import Pagination from '../components/common/Pagination';
import ByAlbumsTable from '../components/ByAlbumsTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';
import SectionTabs from '../components/SectionTabs';
import { Users, Disc, Music, Globe, User, Library, ListMusic, Package, Boxes } from 'lucide-react';
import usePhysicalExists from '../hooks/usePhysicalExists';

function ByMonthAlbumsPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const isAdmin = me?.adminYn === 'Y';
  const [loading, setLoading] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [month, setMonth] = useState('');
  const [monthList, setMonthList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  // 월 리스트 가져오기
  useEffect(() => {
    const fetchMonths = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/ByClient/getMonthList`
        );
        const months = res.data || [];
        setMonthList(months);

        if (!month && months.length > 0) {
          setMonth(months[0]); // 최신 월 자동 설정
        }
      } catch (err) {
        console.error('월 리스트 조회 오류:', err);
      }
    };
    fetchMonths();
  }, [month]);

  // 앨범 데이터 가져오기
  useEffect(() => {
    if (!me || !month) return;
    const clientId = me.id;
    const adminYn = me.adminYn === 'Y' ? 'Y' : 'N';

    const fetchAlbums = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/ByAlbum/byMonthAlbums`,
          {
            params: {
              search: searchKeyword,
              page: currentPage,
              limit: pageSize,
              month: month,
              clientId,
              adminYn,
            },
          }
        );

        const items = response.data.items;
        setAlbums(items);
        setTotalPages(response.data.totalPages);
        setTotalCount(response.data.totalCount);
      } catch (error) {
        console.error('서버 접속 에러:', error);
      } finally {
        setLoading(false);
      }
    };

    if (month) {
      fetchAlbums();
    }
  }, [me, searchKeyword, currentPage, month]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const currentIndex = monthList.indexOf(month);
  const handlePrevMonth = () => {
    if (currentIndex < monthList.length - 1) {
      setMonth(monthList[currentIndex + 1]);
      setCurrentPage(1);
    }
  };
  const handleNextMonth = () => {
    if (currentIndex > 0) {
      setMonth(monthList[currentIndex - 1]);
      setCurrentPage(1);
    }
  };

  const formatMonth = (monthStr) => {
    const [year, m] = monthStr.split('-');
    return `${year}년 ${parseInt(m, 10)}월 앨범별 정산`;
  };

  // 당월 앨범별 엑셀 다운로드
  const handleExcelDownload = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/ByAlbum/byMonthAlbums`,
        {
          params: {
            search: searchKeyword,
            page: 1,
            limit: 10000,
            month,
            clientId: me.id,
            adminYn: me.adminYn === 'Y' ? 'Y' : 'N',
          },
        }
      );
      const items = response.data.items || [];
      if (items.length === 0) { alert('다운로드할 데이터가 없습니다.'); return; }

      const exportData = items.map((item, i) => ({
        순번: i + 1,
        앨범명: item.album?.nameKr || item['album.nameKr'] || '',
        아티스트: item.album?.artist?.nameKr || item['album.artist.nameKr'] || '',
        스트리밍: Number(item.albumHitCount || 0),
        정산액: Number(item.albumAmount || 0),
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '당월 앨범별 정산');
      XLSX.writeFile(wb, `${month?.slice(0, 7)}_앨범별정산.xlsx`);
    } catch (err) {
      console.error('엑셀 다운로드 오류:', err);
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    }
  };

  const isPrevDisabled =
    currentIndex === -1 || currentIndex === monthList.length - 1;
  const isNextDisabled = currentIndex === -1 || currentIndex === 0;

  const hasPhysical = usePhysicalExists();
  const reportTabs = [
    { label: isAdmin ? '당월권리사별' : '월별리포트', to: '/ByClientLatestMonth', icon: Users },
    { label: '당월앨범별', to: '/ByMonthAlbums', icon: Disc },
    { label: '당월곡별', to: '/ByMonthSongs', icon: Music },
    { label: '당월플랫폼별', to: '/ByMonthPlatforms', icon: Globe },
    { label: '당월아티스트별', to: '/ByMonthArtists', icon: User },
    ...(hasPhysical ? [{ label: '당월음반별', to: '/ByPhysicalMonth', icon: Package }] : []),
    { label: '앨범별누적', to: '/ByAlbums', icon: Library },
    { label: '곡별누적', to: '/BySongs', icon: ListMusic },
    ...(hasPhysical ? [{ label: '음반누적', to: '/ByPhysicalTotal', icon: Boxes }] : []),
  ];

  return (
    <>
        {/* 월 이동 */}
        <div className="flex mb-8 justify-center items-center gap-5">
          <button
            onClick={handlePrevMonth}
            disabled={isPrevDisabled}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
            {month ? formatMonth(month) : ''}
          </h1>
          <button
            onClick={handleNextMonth}
            disabled={isNextDisabled}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </div>
        <SectionTabs items={reportTabs} />

        {/* 검색창 */}
        <SearchInput
          placeholder="앨범명 또는 아티스트명 검색"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onSearch={() => {
            setCurrentPage(1); // 검색 시 페이지 초기화
            setSearchKeyword(searchTerm); // 실제 검색 실행
          }}
        />

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3"></div>
            <div className="text-sm text-slate-500">데이터 불러오는 중...</div>
          </div>
        ) : (
          <>
            {/* 테이블 */}
            <ByAlbumsTable
              data={albums}
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              me={me}
              month={month}
            />

            {/* 페이지네이션 */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />

            {/* 엑셀 다운로드 버튼 */}
            {isAdmin && (
              <div className="w-full flex justify-end mt-6">
                <button
                  onClick={handleExcelDownload}
                  className="flex items-center px-4 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-sm font-semibold transition-colors">
                  <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                  다운로드
                </button>
              </div>
            )}
          </>
        )}
    </>
  );
}

export default ByMonthAlbumsPage;
